import React, { useState } from 'react';
import { ProjectCard } from './ProjectCard';
import { ProjectModal } from './ProjectModal';
import { ProjectDetailsModal } from './ProjectDetailsModal';
import { Search, Filter, SlidersHorizontal } from 'lucide-react';
interface Project {
  id: number;
  title: string;
  description: string;
  status: string;
  dueDate: string;
  progress: number;
}
// Sample project data
const initialProjects: Project[] = [{
  id: 1,
  title: 'Refonte du site web',
  description: "Modernisation de l'interface utilisateur et amélioration de l'expérience utilisateur",
  status: 'En cours',
  dueDate: '2023-12-15',
  progress: 65
}, {
  id: 2,
  title: 'Application mobile',
  description: "Développement d'une application mobile pour les clients",
  status: 'Planifié',
  dueDate: '2024-02-28',
  progress: 20
}, {
  id: 3,
  title: 'Système de gestion de contenu',
  description: "Mise en place d'un CMS pour gérer facilement le contenu du site",
  status: 'Terminé',
  dueDate: '2023-10-30',
  progress: 100
}, {
  id: 4,
  title: 'Intégration API',
  description: 'Connexion avec les systèmes externes via API',
  status: 'En cours',
  dueDate: '2023-11-15',
  progress: 45
}, {
  id: 5,
  title: 'Optimisation SEO',
  description: 'Amélioration du référencement naturel',
  status: 'Planifié',
  dueDate: '2024-01-20',
  progress: 10
}];
export function ProjectList() {
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Tous');
  const [sortBy, setSortBy] = useState<string>('date');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  // Filter and sort projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) || project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'Tous' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    } else if (sortBy === 'progress') {
      return b.progress - a.progress;
    } else if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });
  const handleCreateProject = (project: Omit<Project, 'id'>) => {
    const newProject = {
      ...project,
      id: Math.max(...projects.map(p => p.id)) + 1
    };
    setProjects([...projects, newProject]);
    setIsCreateModalOpen(false);
  };
  const handleUpdateProject = (updatedProject: Project) => {
    setProjects(projects.map(p => p.id === updatedProject.id ? updatedProject : p));
    setEditingProject(null);
  };
  const handleDeleteProject = (id: number) => {
    setProjects(projects.filter(p => p.id !== id));
  };
  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setIsDetailsModalOpen(true);
  };
  const handleEditClick = (project: Project) => {
    setEditingProject(project);
  };
  return <div data-id="element-94">
      {/* Search and Filter Bar */}
      <div className="mb-6 space-y-4" data-id="element-95">
        <div className="flex justify-between items-center" data-id="element-96">
          <h2 className="text-xl font-semibold" data-id="element-97">Liste des projets</h2>
          <button onClick={() => setIsCreateModalOpen(true)} className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors" data-id="element-98">
            + Nouveau projet
          </button>
        </div>
        {/* Search Bar */}
        <div className="relative" data-id="element-99">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} data-id="element-100" />
          <input type="text" placeholder="Rechercher un projet..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-101" />
        </div>
        {/* Filters and Sort */}
        <div className="flex flex-wrap gap-3 items-center" data-id="element-102">
          <div className="flex items-center gap-2" data-id="element-103">
            <Filter size={18} className="text-muted-foreground" data-id="element-104" />
            <span className="text-sm font-medium" data-id="element-105">Statut:</span>
            {['Tous', 'En cours', 'Planifié', 'Terminé'].map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`px-3 py-1 text-sm rounded-full transition-colors ${statusFilter === status ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`} data-id="element-106">
                {status}
              </button>)}
          </div>
          <div className="flex items-center gap-2 ml-auto" data-id="element-107">
            <SlidersHorizontal size={18} className="text-muted-foreground" data-id="element-108" />
            <span className="text-sm font-medium" data-id="element-109">Trier par:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-110">
              <option value="date" data-id="element-111">Date d'échéance</option>
              <option value="progress" data-id="element-112">Progression</option>
              <option value="name" data-id="element-113">Nom</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? <div className="text-center py-12" data-id="element-114">
          <p className="text-muted-foreground" data-id="element-115">Aucun projet trouvé</p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-116">
          {filteredProjects.map(project => <ProjectCard key={project.id} project={project} onViewDetails={handleViewDetails} onEdit={handleEditClick} onDelete={handleDeleteProject} data-id="element-117" />)}
        </div>}

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || editingProject) && <ProjectModal project={editingProject} onClose={() => {
      setIsCreateModalOpen(false);
      setEditingProject(null);
    }} onSave={editingProject ? handleUpdateProject : handleCreateProject} data-id="element-118" />}

      {/* Details Modal */}
      {isDetailsModalOpen && selectedProject && <ProjectDetailsModal project={selectedProject} onClose={() => {
      setIsDetailsModalOpen(false);
      setSelectedProject(null);
    }} onEdit={() => {
      setIsDetailsModalOpen(false);
      setEditingProject(selectedProject);
    }} data-id="element-119" />}
    </div>;
}